import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { getMyPermissions, type MyPermissions } from '@/api/access';
import { useAuthContext } from '@/context/AuthContext';
import { checkAccess } from '@/utils/scope';

export const PERMISSIONS_QUERY_KEY = ['me', 'permissions'] as const;
const STALE_TIME_MS = 60_000;

// Same mapping as Grant.CheckRequest in the backend: GET, HEAD and OPTIONS read, other HTTP
// methods write. A scope method ("read", "write", "*") passes through unchanged.
function toScopeMethod(method: string): string {
  switch (method.toUpperCase()) {
    case 'GET':
    case 'HEAD':
    case 'OPTIONS':
      return 'read';
    case 'POST':
    case 'PUT':
    case 'PATCH':
    case 'DELETE':
      return 'write';
    default:
      return method;
  }
}

export function usePermissionCheck() {
  const { token } = useAuthContext();

  const query = useQuery<MyPermissions>({
    queryKey: PERMISSIONS_QUERY_KEY,
    queryFn: () => getMyPermissions(),
    enabled: token !== null,
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: true,
  });

  // Fail closed: while loading or on error there is no data, so every check is false.
  const data = query.isError ? undefined : query.data;

  const isSystemAdmin = useCallback((): boolean => data?.isSystemAdmin === true, [data]);

  const hasPermission = useCallback(
    (key: string): boolean => {
      if (!data) {
        return false;
      }

      return data.isSystemAdmin || (data.permissions ?? []).includes(key);
    },
    [data],
  );

  const hasAnyPermissions = useCallback(
    (keys: string[]): boolean => keys.some((key) => hasPermission(key)),
    [hasPermission],
  );

  const canCall = useCallback(
    (method: string, path: string): boolean => {
      if (!data) {
        return false;
      }

      return checkAccess(data.scopes ?? [], toScopeMethod(method), path).allowed;
    },
    [data],
  );

  return {
    hasPermission,
    hasAnyPermissions,
    isSystemAdmin,
    canCall,
    isLoading: query.isLoading,
  };
}
